///
/// EarthBrowser
///
/// Created by Matt Giger on 10/23/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Render-to-texture pass that rasterizes the aurora hex mesh into the
/// two-hemisphere stereographic probability field (aurora_field.glsl) the
/// volume shader marches through. Runs from the node's render traversal,
/// before the frame's draw calls execute, so it restores the GL state it
/// touches. A bake happens only when the frame pair in the mesh's value
/// streams changes, not per rendered frame.
///

import { type HexGridGeometry } from "@/earthbrowser/dataset-distribution/aurora/HexGridGeometry"
import { FrameBuffer } from "@stovokor/shader/FrameBuffer"
import { ComponentType, DrawMode, InternalFormat, PixelFormat, TextureFilter, TextureWrap } from "@stovokor/shader/GLConstants"
import { type ShaderProgram } from "@stovokor/shader/ShaderProgram"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { Texture } from "@stovokor/shader/Texture"
import { type StovokorContext } from "@stovokor/StovokorContext"

export class AuroraFieldBaker {
	private context: StovokorContext
	private shader: ShaderProgram
	private framebuffer: FrameBuffer

	constructor(context: StovokorContext) {
		this.context = context
		this.shader = context.getCachedShader(new ShaderSourceDefinition("aurora_field"))
		this.framebuffer = new FrameBuffer(context)
	}

	public destroy(): void {
		this.framebuffer.destroy()
	}

	/**
	 * RG8 target holding both hemispheres side by side at `hemispherePx`
	 * per hemisphere. Mipmaps are built after each bake: the volume shader
	 * reads a coarse level to reject rays that pass nowhere near the oval
	 */
	public createTarget(hemispherePx: number): Texture {
		return new Texture(this.context, hemispherePx * 2, hemispherePx,
			InternalFormat.RG8, PixelFormat.RG, ComponentType.UnsignedByte,
			TextureFilter.Linear, TextureFilter.Linear,
			TextureWrap.ClampToEdge, TextureWrap.ClampToEdge)
	}

	/**
	 * Rasterize the mesh's active cells into the target. Returns false,
	 * leaving the target untouched, if the pass can't run yet (shader still
	 * compiling), so the caller retries next frame.
	 */
	public bake(geometry: HexGridGeometry, target: Texture): boolean {
		if (!this.shader.available || !target.textureId) {
			return false
		}

		const gl = this.context.gl
		// Save/restore through the engine's state cache: no getParameter /
		// isEnabled round trips, and the cache stays exact for the next frame
		const glState = this.context.glState
		const viewport = glState.getViewport()
		const cullWasOn = glState.isEnabled(WebGL2RenderingContext.CULL_FACE)
		const scissorWasOn = glState.isEnabled(WebGL2RenderingContext.SCISSOR_TEST)

		try {
			this.framebuffer.attach(WebGL2RenderingContext.COLOR_ATTACHMENT0, target)
			this.framebuffer.bind()
		} catch (error) {
			this.framebuffer.unbind()
			this.context.logger.warn("AuroraFieldBaker: framebuffer incomplete", error)
			return false
		}
		glState.viewport(0, 0, target.width, target.height)
		glState.disable(WebGL2RenderingContext.DEPTH_TEST)
		glState.disable(WebGL2RenderingContext.BLEND)
		glState.disable(WebGL2RenderingContext.CULL_FACE)
		glState.disable(WebGL2RenderingContext.SCISSOR_TEST)
		gl.clearColor(0, 0, 0, 0)
		gl.clear(WebGL2RenderingContext.COLOR_BUFFER_BIT)

		this.shader.bindAction()()
		this.shader.setupAttributeActionsFrom(geometry).forEach(action => action())
		geometry.indexBuffer.bind()
		geometry.indexBuffer.draw(DrawMode.Triangles)
		geometry.indexBuffer.unbind()
		this.shader.disableAttributeActions().forEach(action => action())
		this.shader.unbindAction()()

		this.framebuffer.unbind()
		glState.enable(WebGL2RenderingContext.DEPTH_TEST)
		glState.enable(WebGL2RenderingContext.BLEND)
		glState.setEnabled(WebGL2RenderingContext.CULL_FACE, cullWasOn)
		glState.setEnabled(WebGL2RenderingContext.SCISSOR_TEST, scissorWasOn)
		glState.viewport(viewport[0], viewport[1], viewport[2], viewport[3])

		target.generateMipmaps()
		target.available = true
		return true
	}
}
